import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { JSTClock } from './JSTClock';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { language, switchLanguage } = useLanguage();
  const location = useLocation();

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-lg md:max-w-2xl lg:max-w-6xl xl:max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Logo + Navigation Links */}
            <div className="flex items-center gap-4">
              {/* Logo/Brand */}
              <Link to="/" className="text-xl font-bold text-gray-900 hover:text-blue-600 transition-colors inline-flex items-center gap-2">
                <img src="/logo512.png" alt="Go Seto Logo" className="h-10 w-10" />
                Go Seto
              </Link>

              {/* Desktop Navigation Links with separator */}
              <nav className="hidden sm:flex items-baseline gap-4">
                <span className="text-gray-300">|</span>
                <Link
                  to="/triennale"
                  className={`text-sm font-medium transition-all border-b-2 ${
                    location.pathname === '/triennale'
                      ? 'text-blue-600 border-blue-600'
                      : 'text-gray-700 hover:text-blue-600 border-transparent hover:border-blue-300'
                  } ${language === 'zh' ? 'font-chinese-sans' : language === 'ja' ? 'font-japanese-sans' : 'font-sans'}`}
                >
                  {language === 'zh' ? '2025濑户内海艺术祭信息' : language === 'ja' ? '2025瀬戸内国際芸術祭情報' : '2025 Setouchi Triennale info'}
                </Link>
                <span className="text-gray-300">|</span>
                <Link
                  to="/about"
                  className={`text-sm font-medium transition-all border-b-2 ${
                    location.pathname === '/about'
                      ? 'text-blue-600 border-blue-600'
                      : 'text-gray-700 hover:text-blue-600 border-transparent hover:border-blue-300'
                  } ${language === 'zh' ? 'font-chinese-sans' : language === 'ja' ? 'font-japanese-sans' : 'font-sans'}`}
                >
                  {language === 'zh' ? '关于我们' : language === 'ja' ? '私たちについて' : 'About Us'}
                </Link>
              </nav>

              {/* Mobile Navigation Dropdown */}
              <div className="sm:hidden relative group">
                <button className="p-2 text-gray-700 hover:text-blue-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 hidden group-hover:block z-50">
                  <Link
                    to="/"
                    className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${language === 'zh' ? 'font-chinese-sans' : language === 'ja' ? 'font-japanese-sans' : 'font-sans'}`}
                  >
                    {language === 'zh' ? '首页' : language === 'ja' ? 'ホーム' : 'Home'}
                  </Link>
                  <Link
                    to="/triennale"
                    className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${language === 'zh' ? 'font-chinese-sans' : language === 'ja' ? 'font-japanese-sans' : 'font-sans'}`}
                  >
                    {language === 'zh' ? '2025濑户内海艺术祭信息' : language === 'ja' ? '2025瀬戸内国際芸術祭情報' : '2025 Setouchi Triennale info'}
                  </Link>
                  <Link
                    to="/about"
                    className={`block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${language === 'zh' ? 'font-chinese-sans' : language === 'ja' ? 'font-japanese-sans' : 'font-sans'}`}
                  >
                    {language === 'zh' ? '关于我们' : language === 'ja' ? '私たちについて' : 'About Us'}
                  </Link>
                </div>
              </div>
            </div>

            {/* Right: Clock + Language Toggle */}
            <div className="flex items-center gap-4">
              {/* Local Time Clock - Hidden on mobile */}
              <div className="hidden md:flex">
                <JSTClock />
              </div>

              {/* Language Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => switchLanguage('en')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  language === 'en'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => switchLanguage('zh')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  language === 'zh'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                中文
              </button>
              <button
                onClick={() => switchLanguage('ja')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  language === 'ja'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                日本語
              </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      {children}
    </div>
  );
};
